import { EffectComposer } from 'postprocessing'
import { useEffect, useRef } from 'react'
import { AnimationMixer, WebGLRenderer } from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import Stats from '@trillionhq/stats.js'
import * as TWEEN from '@tweenjs/tween.js'

import s from './ViewerRenderer.module.scss'
import { ModelControls } from '../../utils/ModelControls'
import { isMobileOrTablet } from '../../../../../trillion-common/src/utils/device-utils'
import { useAppStore } from '../../../shared/store/store'
import { useContainerRef } from '../../hooks/useContainerRef'

interface ViewerRendererProps {
  renderer?: WebGLRenderer
  controls?: ModelControls | OrbitControls
  composer?: EffectComposer
  stats?: Stats
  mixer?: AnimationMixer
  sku: string
}

export const ViewerRenderer = ({
  renderer,
  controls,
  composer,
  stats,
  mixer,
  sku,
}: ViewerRendererProps): JSX.Element => {
  const [container, onContainerRefChange] = useContainerRef()
  const isStopped = useAppStore((state) => state.isStopped)
  const background = useAppStore((state) => state.background)
  const setIsStopped = useAppStore((state) => state.setIsStopped)
  const rotateObject = useAppStore((state) => state.rotateObject)
  const reducePixelRatio = useAppStore((state) => state.reducePixelRatio)

  const rafRef = useRef<number>()

  const devicePixelRatio = window.devicePixelRatio
  const isMobileDevice = isMobileOrTablet()

  let prevTime = 0

  useEffect(() => {
    if (!container || !renderer) {
      return
    }

    const rendererElement = renderer.domElement

    if (!container.contains(rendererElement)) {
      container.appendChild(rendererElement)
    }

    if (reducePixelRatio) {
      rendererElement.addEventListener(isMobileDevice ? 'touchstart' : 'mousedown', () => {
        renderer.setPixelRatio(1)
      })
      rendererElement.addEventListener(isMobileDevice ? 'touchend' : 'mouseup', () => {
        renderer.setPixelRatio(devicePixelRatio)
      })
    }

    stats?.begin()
  }, [container, renderer, stats])

  useEffect(() => {
    if (!isStopped) {
      animate(0, true)
    }
  }, [isStopped, mixer])

  const disposeAll = () => {
    setIsStopped(true)
    if (controls) {
      controls.enabled = false

      if (rotateObject && controls instanceof ModelControls) {
        controls.detach()
      }

      controls.dispose()
    }

    stats?.end()

    if (mixer) {
      mixer.stopAllAction()
    }

    if (reducePixelRatio) {
      renderer?.domElement.removeEventListener(isMobileDevice ? 'touchstart' : 'mousedown', () => {
        renderer.setPixelRatio(1)
      })
      renderer?.domElement.removeEventListener(isMobileDevice ? 'touchend' : 'mouseup', () => {
        renderer.setPixelRatio(devicePixelRatio)
      })
    }

    renderer?.domElement.remove()
    renderer?.dispose()
    rafRef.current && cancelAnimationFrame(rafRef.current)
    window.ExposedModelBySKU[sku] = null
    window.ExposedStateBySKU[sku] = null
  }

  function animate(time = 0, forceAnimate = false) {
    if (isStopped && !forceAnimate) {
      return
    }

    if (!controls) {
      throw new Error('Controls is uninitialized')
    }

    if (!composer) {
      throw new Error('Composer is uninitialized')
    }

    if (!stats) {
      throw new Error('Stats is uninitialized')
    }

    if (mixer) {
      mixer.update(Math.abs(time - prevTime) / 1000)
    }
    prevTime = time

    controls.update()
    stats?.update()
    composer.render()
    TWEEN.update(time)
    rafRef.current = requestAnimationFrame(animate)
  }

  useEffect(() => {
    window.disposeAll = disposeAll
    return () => {
      disposeAll()
      window.disposeAll = null
    }
  }, [])

  return <div ref={onContainerRefChange} className={s.ViewerRenderer} style={{ background }}></div>
}
